import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { toISODateOnly } from "@/lib/time";
import { MedicationRequest, MedicationStatement } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

export enum AdherenceStatus {
  ACTIVE = "active",
  STOPPED = "stopped",
}

export enum Action {
  ACTIVE = "ACTIVE",
  INTENDED = "INTENDED",
  NOT_TAKEN = "NOT_TAKEN",
  STOPPED = "STOPPED",
  COMPLETE = "COMPLETE",
  VOID = "VOID",
  CONFIRM = "CONFIRM",
  DENY = "DENY",
}

interface GetMedicationsParams {
  adherenceStatus: AdherenceStatus;
  patientId?: string;
}

interface AvailableAction {
  action: Action;
  unavailableReason?: string;
}

export interface PatientMedication {
  medicationStatement: MedicationStatement;
  needsConfirmation: boolean;
  medicationRequest?: MedicationRequest;
  availableStatusChangeActions: AvailableAction[];
  availableConfirmationActions: AvailableAction[];
}

export interface MedicationHistoryResponse {
  medications: [
    {
      medicationStatement: MedicationStatement;
      medicationRequest?: MedicationRequest;
      needsConfirmation: boolean;
      availableStatusChangeActions: AvailableAction[];
      availableConfirmationActions: AvailableAction[];
    },
  ];
}

export const usePatientMedications = ({
  patientId,
  adherenceStatus,
}: GetMedicationsParams) => {
  return useApi<MedicationHistoryResponse, PatientMedication[]>({
    queryOptions: {
      enabled: Boolean(adherenceStatus && patientId),
      queryKey: QueryKeys.Patient.MedicalRecords.Medications.ADHERENCE_SECTION(
        adherenceStatus,
        patientId,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get("/rx/medications/hx", {
          params: { patientId, adherenceStatus: adherenceStatus },
        });
        return response?.data;
      },
      select: (data): PatientMedication[] => {
        return data?.medications.map((medication) => {
          const medicationStatement = new MedicationStatement(
            medication.medicationStatement,
          );
          const medicationRequest = medication.medicationRequest
            ? new MedicationRequest(medication.medicationRequest)
            : undefined;

          return {
            medicationStatement,
            needsConfirmation: medication.needsConfirmation,
            medicationRequest,
            availableStatusChangeActions:
              medication.availableStatusChangeActions,
            availableConfirmationActions:
              medication.availableConfirmationActions,
          };
        });
      },
    },
  });
};

export interface DrugNameSearchItem {
  drugName: string;
  drugCode: string;
  drugCodeSystem: string;
  personalizedInfo?: {
    previouslyCharted: boolean;
    previousMedicationStatementIds: string[];
  };
}

interface DrugNameSearchResponse {
  medications: DrugNameSearchItem[];
}

export const useSearchDrugs = (
  searchTerm?: string,
  patientId?: string,
  includeDrugs = true,
  includeMedications = true,
) => {
  return useApi<DrugNameSearchResponse, DrugNameSearchItem[]>({
    queryOptions: {
      enabled: Boolean(searchTerm && searchTerm.length > 2),
      queryKey: patientId
        ? QueryKeys.Patient.MedicalRecords.Medications.SEARCH_PERSONALIZED_DRUGS(
            {
              patientId,
              searchTerm: searchTerm as string,
              includeDrugs,
              includeMedications,
            },
          )
        : QueryKeys.Patient.MedicalRecords.Medications.SEARCH_DRUGS({
            searchTerm: searchTerm as string,
            includeDrugs,
            includeMedications,
          }),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/rx/medications/search-drugs",
          {
            params: {
              searchTerm: searchTerm as string,
              patientId: patientId,
              includeDrugs,
              includeMedications,
            },
          },
        );
        return response?.data;
      },
      select: (data) => {
        return data?.medications;
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};

interface CreateMedicationResponse {
  bundle: object;
}
interface CreateMedicationCriteria {
  subjectId: string;
  encounterId?: string;
  medication: {
    coding: [
      {
        system: string;
        code: string;
        display?: string;
      },
    ];
    text?: string;
  };
  medicationStatement: {
    effectivePeriodStart?: string;
    effectivePeriodEnd?: string;
    note?: string;
    status?: string;
  };
}

export const useCreateMedication = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<CreateMedicationResponse, CreateMedicationCriteria>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Medications.CREATE(patientId),
      mutationFn: async (payload: CreateMedicationCriteria) => {
        const response = await axiosInstance.post(
          "/rx/medications/hx",
          payload,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        onSuccess?.();
      },
    },
  });
};

interface UpdateMedicationParams {
  patientId: string;
  medicationStatementId: string;
  onSuccess?: () => void;
}

interface UpdateMedicationCriteria {
  startDate?: Date;
  endDate?: Date;
  note?: string;
  status?:
    | (typeof MedicationStatement.Status.values)[keyof typeof MedicationStatement.Status.values]
    | undefined;
}

interface UpdateMedicationResponse {
  bundle: object;
}

export const useUpdateMedication = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, UpdateMedicationCriteria>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.UPDATE(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async (criteria) => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}`,
          {
            medicationStatement: {
              effectivePeriodStart: toISODateOnly(criteria.startDate),
              effectivePeriodEnd: toISODateOnly(criteria.endDate),
              note: criteria.note,
              status: criteria.status,
            },
          },
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        onSuccess?.();
      },
    },
  });
};

export const useConfirmMedication = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.CONFIRM(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/confirm`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

export const useDenyMedication = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.DENY(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/deny`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        onSuccess?.();
      },
    },
  });
};

export const useBatchConfirmMedications = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, string[]>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Medications.BATCH_CONFIRM(patientId),
      mutationFn: async (medicationStatementIds) => {
        const response = await axiosInstance.put(
          "/rx/medications/hx/batch-confirm",
          {
            medicationStatementIds,
          },
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.ADHERENCE_SECTION(
              AdherenceStatus.ACTIVE,
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationActive = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.ACTIVATE(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/activate`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationIntended = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.INTENDED(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/intended`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationNotTaken = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.NOT_TAKEN(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/not-taken`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationStopped = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.STOPPED(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/stop`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationCompleted = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.COMPLETE(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/complete`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationVoided = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: UpdateMedicationParams) => {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMedicationResponse, void>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Medications.VOID(
        patientId,
        medicationStatementId,
      ),
      mutationFn: async () => {
        const response = await axiosInstance.patch(
          `/rx/medications/hx/${medicationStatementId}/void`,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Medications.DRUG_NAMES,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              query.queryKey.length === 6 &&
              query.queryKey[0] === "patient" &&
              query.queryKey[1] === "chart" &&
              query.queryKey[2] === patientId &&
              query.queryKey[3] === "prescriptions" &&
              query.queryKey[5] === "renewable-prescriptions"
            );
          },
        });
        onSuccess?.();
      },
    },
  });
};

export const useMarkMedicationStatus = ({
  patientId,
  medicationStatementId,
  onSuccess,
}: {
  patientId: string;
  medicationStatementId: string;
  onSuccess?: () => void;
}) => {
  const { isPending: isMarkingActive, mutate: markMedicationActive } =
    useMarkMedicationActive({ patientId, medicationStatementId, onSuccess });
  const { isPending: isMarkingIntended, mutate: markMedicationIntended } =
    useMarkMedicationIntended({ patientId, medicationStatementId, onSuccess });
  const { isPending: isMarkingNotTaken, mutate: markMedicationNotTaken } =
    useMarkMedicationNotTaken({ patientId, medicationStatementId, onSuccess });
  const { isPending: isMarkingStopped, mutate: markMedicationStopped } =
    useMarkMedicationStopped({ patientId, medicationStatementId, onSuccess });
  const { isPending: isMarkingCompleted, mutate: markMedicationCompleted } =
    useMarkMedicationCompleted({ patientId, medicationStatementId, onSuccess });
  const { isPending: isMarkingVoided, mutate: markMedicationVoided } =
    useMarkMedicationVoided({ patientId, medicationStatementId, onSuccess });

  return {
    isSavingStatus:
      isMarkingActive ||
      isMarkingIntended ||
      isMarkingNotTaken ||
      isMarkingStopped ||
      isMarkingCompleted ||
      isMarkingVoided,
    markMedicationActive,
    markMedicationIntended,
    markMedicationNotTaken,
    markMedicationStopped,
    markMedicationCompleted,
    markMedicationVoided,
  };
};

export enum MedicationChartingAction {
  DOCUMENT_MEDICATION = "DOCUMENT_MEDICATION",
  CONFIRM_ALL_MEDICATIONS = "CONFIRM_ALL_MEDICATIONS",
  RETRY_FAILED_SUBMISSION = "RETRY_FAILED_SUBMISSION",
  MARK_PHONED_IN = "MARK_PHONED_IN",
}

export enum MedicationChartingActionNotAvailableReason {
  NO_PRIVILEGES = "NO_PRIVILEGES",
}

interface AvailableChartingAction {
  action: MedicationChartingAction;
  unavailableReason?: MedicationChartingActionNotAvailableReason;
}

interface GetAvailableMedicationActionsResponse {
  availableChartingActions: AvailableChartingAction[];
}

export const useGetMedicationChartingActions = (patientId: string) => {
  return useApi<
    GetAvailableMedicationActionsResponse,
    AvailableChartingAction[]
  >({
    queryOptions: {
      enabled: true,
      queryKey:
        QueryKeys.Patient.MedicalRecords.Medications.AVAILABLE_CHARTING_ACTIONS(
          patientId,
        ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/rx/medications/available-actions",
        );
        return response?.data;
      },
      select: (data) => {
        return data?.availableChartingActions;
      },
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
};
